/**
 * HierarchyTreePicker — pick one node from any hierarchy kind (org, product).
 *
 * Kind-agnostic sibling of OrgUnitTreePicker: that component carries the org
 * chart's single-root canon rules and stays as is; this one works over the
 * shared HierarchyNode shape so the product tree (and any later kind) gets a
 * picker without another bespoke tree. Same keyboard model: rows are
 * buttons, Enter/Space selects, the chevron toggles.
 */

import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { Button } from '../ui/Button';
import {
  buildHierarchyTree,
  formatHierarchyPathLabel,
  type HierarchyNode,
  type HierarchyTreeNode,
} from '../../lib/hierarchy/hierarchyTypes';

interface HierarchyTreePickerProps<N extends HierarchyNode> {
  nodes: readonly N[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Ids that cannot be chosen (e.g. a node and its descendants while moving it). */
  disabledIds?: ReadonlySet<string>;
  /** Label for the "no node" row; omit to hide it. */
  noneLabel?: string;
  /** Per-node trailing decoration (a level badge, a count). */
  renderMeta?: (node: N) => ReactNode;
  emptyMessage?: string;
  className?: string;
}

function Row<N extends HierarchyNode>({
  tree,
  depth,
  selectedId,
  disabledIds,
  onSelect,
  renderMeta,
}: {
  tree: HierarchyTreeNode<N>;
  depth: number;
  selectedId: string | null;
  disabledIds?: ReadonlySet<string>;
  onSelect: (id: string) => void;
  renderMeta?: (node: N) => ReactNode;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const { node, children } = tree;
  const hasChildren = children.length > 0;
  const disabled = disabledIds?.has(node.id) ?? false;
  const selected = selectedId === node.id;

  const choose = () => {
    if (!disabled) onSelect(node.id);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose();
    }
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-pressed={selected}
        onClick={choose}
        onKeyDown={onKey}
        style={{ paddingLeft: 8 + depth * 16 }}
        className={`w-full flex items-center gap-1.5 pr-2 py-1.5 text-sm text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 ${
          disabled
            ? 'text-[var(--text-dim)] cursor-not-allowed'
            : selected
              ? 'bg-primary-50 text-primary-700 font-medium dark:bg-[rgba(212,160,23,0.12)] dark:text-[var(--mustard)] cursor-pointer'
              : 'text-[var(--text-pri)] hover:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)] cursor-pointer'
        }`}
      >
        <Button
          variant="ghost"
          size="sm"
          tabIndex={-1}
          aria-label={hasChildren ? (expanded ? 'Collapse' : 'Expand') : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) setExpanded((v) => !v);
          }}
          className={`!p-0 !gap-0 !h-4 flex-shrink-0 w-4 ${
            hasChildren ? 'text-[var(--text-ter)] hover:text-[var(--text-sec)]' : 'cursor-default hover:bg-transparent'
          }`}
        >
          {hasChildren ? (
            expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
          ) : null}
        </Button>
        <span className="truncate flex-1 min-w-0">{node.name}</span>
        {renderMeta ? <span className="flex-shrink-0">{renderMeta(node)}</span> : null}
      </div>
      {hasChildren && expanded && (
        <div>
          {children.map((c) => (
            <Row
              key={c.node.id}
              tree={c}
              depth={depth + 1}
              selectedId={selectedId}
              disabledIds={disabledIds}
              onSelect={onSelect}
              renderMeta={renderMeta}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function HierarchyTreePicker<N extends HierarchyNode>({
  nodes,
  selectedId,
  onSelect,
  disabledIds,
  noneLabel,
  renderMeta,
  emptyMessage = 'No nodes yet.',
  className = '',
}: HierarchyTreePickerProps<N>) {
  const tree = useMemo(() => buildHierarchyTree(nodes), [nodes]);
  const selectedPath = useMemo(
    () => (selectedId ? formatHierarchyPathLabel(selectedId, nodes) : ''),
    [selectedId, nodes],
  );

  return (
    <div className={`border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] ${className}`}>
      {selectedPath ? (
        <div className="px-2 py-1.5 border-b border-[var(--rule)] text-xs text-[var(--text-sec)] flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
          <span className="truncate">{selectedPath}</span>
        </div>
      ) : null}
      <div className="max-h-56 overflow-y-auto py-1">
        {noneLabel ? (
          <div
            role="button"
            tabIndex={0}
            aria-pressed={selectedId == null}
            onClick={() => onSelect(null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(null);
              }
            }}
            className={`px-2 py-1.5 text-sm cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              selectedId == null
                ? 'bg-primary-50 text-primary-700 font-medium dark:bg-[rgba(212,160,23,0.12)] dark:text-[var(--mustard)]'
                : 'text-[var(--text-ter)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
            }`}
          >
            {noneLabel}
          </div>
        ) : null}
        {tree.length === 0 ? (
          <p className="px-2 py-2 text-xs text-[var(--text-ter)]">{emptyMessage}</p>
        ) : (
          tree.map((t) => (
            <Row
              key={t.node.id}
              tree={t}
              depth={0}
              selectedId={selectedId}
              disabledIds={disabledIds}
              onSelect={(id) => onSelect(id)}
              renderMeta={renderMeta}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default HierarchyTreePicker;
